import React from 'react';
import { formatPrice } from '@/utils/formatPrice';
import { formatBillingDate } from '@/utils/formatBillingDate';
import type { SubscriptionTerms } from '@/services/billing/subscriptionTerms';
import { openerWait } from '@/services/billing/buildSignupSummary';

const Bold = ({ children }: { children: React.ReactNode }) => (
  <span className="font-medium text-fg dark:text-text-primary tabular-nums">{children}</span>
);

/** Disclosure paragraph: minimum ARL §17602(a)(1) elements (amount, cadence,
 *  end, cancel) inline; per-sport detail in the order-summary aside above.
 *  Shared by CheckoutForm and AddSportDialog so the legal copy is single-sourced. */
export function SubscriptionDisclosure({
  terms,
  firstChargeCents,
}: {
  terms: SubscriptionTerms;
  firstChargeCents: number;
}): React.ReactNode {
  const tos = (
    <a
      href="/terms"
      target="_blank"
      rel="noopener"
      className="underline decoration-border-interactive hover:decoration-brand-500 underline-offset-2 text-fg dark:text-text-primary"
    >
      Terms of Service
    </a>
  );

  const action = terms.isAddSport
    ? 'adding to your subscription'
    : terms.isTrial ? 'starting your trial' : 'subscribing';
  // Pre-sale: coverage waits for the opener, so name the dates — "after your free trial"
  // alone would read as N days from today, and "today" would be false without a trial.
  const wait = openerWait(terms.isTrial, terms.coverageStartDate?.getTime());
  const when = wait?.kind === 'trial' ? (
    <>
      on <Bold>{formatBillingDate(terms.recurringStartDate)}</Bold>, after your {terms.trialDays}-day
      free trial, which starts on opening night (<Bold>{formatBillingDate(new Date(wait.startMs))}</Bold>)
    </>
  ) : wait?.kind === 'billing' ? (
    <>
      on opening night (<Bold>{formatBillingDate(new Date(wait.startMs))}</Bold>) — nothing is
      charged today —
    </>
  ) : terms.isTrial ? 'after your free trial' : 'today';

  // Season tail: rest of season free; first charge is at next season's resume —
  // a date that doesn't exist yet (admin rolls sport_seasons later), so the
  // pre-resume notice email carries the concrete date per CA-ARL.
  const hasTail = terms.tailSports.length > 0;
  const pureTail = hasTail && terms.tailSports.length === terms.sports.length;
  const tailLabel = terms.tailSports.map((s) => s.toUpperCase()).join(' + ');
  if (pureTail) {
    return (
      <>
        By {action}, you pay <Bold>nothing today</Bold> — the rest of this season is free. Your
        subscription auto-renews at <Bold>{formatPrice(terms.tailMonthlyCents)}</Bold>/month plus
        applicable tax starting when the next {tailLabel} season begins; we&apos;ll email you 7 days
        before your first charge. Billing pauses during the off-season, and you can cancel anytime.
        See our {tos}.
      </>
    );
  }
  const tailNote = hasTail ? (
    <>
      {' '}{tailLabel} is free for the rest of this season, then{' '}
      <Bold>{formatPrice(terms.tailMonthlyCents)}</Bold>/month when the next season begins
      (we&apos;ll email you 7 days before that charge).
    </>
  ) : null;

  return (
    <>
      You authorize <Bold>{formatPrice(firstChargeCents)}</Bold> plus tax {when}, then monthly unless
      canceled. Billing pauses during the off-season.{tailNote} See our {tos}.
    </>
  );
}
